// ── Resource detail ("/resources/:resource_id") — Wave 8 ────────────────────
// The full resource record: an identity header (title + category + scope +
// status) over the description, the open-link, the manage panel (STAFF-only:
// edit/attach + archive not-configured), the file-storage panel (503 gate-closed),
// and the download panel (role/category-gated + 503 gate-closed). The resource is
// read via the scoped detail endpoint; the id is a STRING — passed VERBATIM,
// never Number().
//
// ── PARTICIPANT EXCLUSION (Wave 8 release gate) ──────────────────────────────
// The route manifest keeps /resources/:id at resourceModuleRoles (participant
// excluded → generic-not-found). THIS page ALSO fail-closes
// (canAccessResourcesModule): a participant who somehow reached the component
// renders ONLY permission-denied with NO resource, and the resource read NEVER
// fires for a participant (`enabled` gates the query).

import { Badge, DetailSurface } from "@vjal/ui";

import { Link } from "react-router-dom";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import { liveRecordIdFromPath } from "../product-live-rows";
import type { CoachingResourceDto } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import {
  canAccessResourcesModule,
  isValidResourceId,
  orDash,
  resourceCategoryLabels,
  resourceCategoryOf,
  resourceScopeLabel,
  resourceStatusLabel
} from "./resources-helpers";
import { ResourceManageSection } from "./resource-detail/resource-manage-section";
import { ResourceUploadSection } from "./resource-detail/resource-upload-section";
import { ResourceDownloadSection } from "./resource-detail/resource-download-section";

const services = getCoachingServices();

export function ResourceDetailPage({ role, routePath }: ProductScreenContext) {
  const resourceId = liveRecordIdFromPath(routePath, "resources");
  const validId = isValidResourceId(resourceId);
  const moduleDenied = !canAccessResourcesModule(role);

  const query = useHonestQuery<CoachingResourceDto>(
    coachingQueryKeys.resourceDetail(resourceId ?? "unknown"),
    () => services.resources.detail(resourceId as string),
    { enabled: validId && !moduleDenied, isEmpty: (data) => !data }
  );

  // ── Fail closed: a denied role gets permission-denied, NO resource. ─────────
  if (moduleDenied) {
    return (
      <section aria-label="Resource detail" className="col-span-12" data-testid="resource-detail-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture="permission-denied"
            title="You don't have access to this resource"
            message="The resources admin is for coaching staff and assigned leads. Resources shared with you appear on your dashboard."
          />
        </div>
      </section>
    );
  }

  const resource = query.data;
  if (!validId || query.posture !== null || !resource) {
    const posture = !validId ? "empty" : query.posture ?? (query.data ? "empty" : "loading");
    const notFound = posture === "empty";
    return (
      <section aria-label="Resource detail" className="col-span-12" data-testid="resource-detail-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture={posture}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            title={notFound ? "Resource not found" : undefined}
            message={
              notFound
                ? "This resource doesn't exist, or it isn't one you're authorized to see."
                : undefined
            }
            canCreate={notFound}
            createAction={
              notFound ? (
                <Link to="/resources" className="text-link no-underline hover:underline">
                  Back to resources
                </Link>
              ) : undefined
            }
          />
        </div>
      </section>
    );
  }

  const category = resourceCategoryOf(resource);

  return (
    <section aria-label="Resource detail" className="col-span-12 flex flex-col gap-4" data-testid="resource-detail-page">
      {/* F3: real @vjal/ui DetailSurface for Detail-1 resource-detail. */}
      <DetailSurface
        title={
          <span className="flex flex-wrap items-center gap-3">
            {orDash(resource.title)}
            <Badge variant="info">{resourceCategoryLabels[category]}</Badge>
            <Badge variant="success">{resourceScopeLabel(resource)}</Badge>
          </span>
        }
        fields={[
          {
            id: "back",
            label: "Directory",
            value: (
              <Link to="/resources" className="text-link no-underline hover:underline">
                ← All resources
              </Link>
            ),
          },
          {
            id: "description",
            label: "Description",
            value: orDash(resource.description),
          },
          {
            id: "category",
            label: "Category",
            value: resourceCategoryLabels[category],
          },
          {
            id: "scope",
            label: "Scope",
            value: resourceScopeLabel(resource),
          },
          {
            id: "status",
            label: "Status",
            value: resourceStatusLabel(resource.status),
          },
          ...(resource.url
            ? [
                {
                  id: "url",
                  label: "Link",
                  value: (
                    <a className="text-link no-underline hover:underline" href={resource.url} target="_blank" rel="noopener noreferrer">
                      Open resource link
                    </a>
                  ),
                },
              ]
            : []),
        ]}
        titleExactName="Resource title">
        <div className="mt-4 grid grid-cols-12 gap-4">
          <ResourceManageSection resource={resource} role={role} />
          <ResourceUploadSection role={role} />
          <ResourceDownloadSection resource={resource} role={role} />
        </div>
      </DetailSurface>
    </section>
  );
}
